import React, { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Move } from 'lucide-react';

// Default section order
export const DEFAULT_SECTION_ORDER = [
  'department-scorecard',
  'weekly-required-output',
  'marketing-efficiency',
  'targets-pacing',
  'market-performance',
  'weekly-pacing-grid',
  'integrations-panel'
];

// Section metadata for display
export const SECTION_METADATA = {
  'department-scorecard': { title: 'Department Scorecard', icon: 'LayoutDashboard' },
  'weekly-required-output': { title: 'Weekly Required Output', icon: 'Target' },
  'marketing-efficiency': { title: 'Marketing Efficiency', icon: 'TrendingUp' },
  'targets-pacing': { title: 'Targets & Pacing', icon: 'Goal' },
  'market-performance': { title: 'Market Performance', icon: 'BarChart3' },
  'weekly-pacing-grid': { title: 'Weekly Pacing Grid', icon: 'Calendar' },
  'integrations-panel': { title: 'Integrations', icon: 'Link2' }
};

// localStorage key for persisting section order
const STORAGE_KEY = 'ontrack-pacing-section-order';

// Get saved section order from localStorage
export const getSavedSectionOrder = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that all sections are present
      if (parsed.length === DEFAULT_SECTION_ORDER.length && 
          DEFAULT_SECTION_ORDER.every(s => parsed.includes(s))) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load section order:', e);
  }
  return DEFAULT_SECTION_ORDER;
};

// Save section order to localStorage
export const saveSectionOrder = (order) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  } catch (e) {
    console.error('Failed to save section order:', e);
  }
};

// Sortable item wrapper component
const SortableSection = ({ id, children, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSorting
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSorting ? 0.5 : 1,
    position: 'relative'
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="group relative"
      data-testid={`draggable-section-${id}`}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute left-0 top-0 z-10 p-2 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-slate-800/90 rounded-tr-lg rounded-bl-lg shadow-sm border border-slate-200 dark:border-slate-700"
        title="Drag to reorder"
        data-testid={`drag-handle-${id}`}
      >
        <GripVertical className="h-5 w-5 text-slate-400 dark:text-slate-500" />
      </div>
      
      {/* Section Content */}
      <div className={`${isSorting ? 'ring-2 ring-primary ring-offset-2' : ''}`}>
        {children}
      </div>
    </div>
  );
};

// Main DraggableSections container
const DraggableSections = ({ sectionOrder, onReorder, children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = sectionOrder.indexOf(active.id);
      const newIndex = sectionOrder.indexOf(over.id);
      const newOrder = arrayMove(sectionOrder, oldIndex, newIndex);
      onReorder(newOrder);
      saveSectionOrder(newOrder);
    }
  };

  // Map children by their section ID
  const childrenMap = useMemo(() => {
    const map = {};
    React.Children.forEach(children, (child) => {
      if (child && child.props && child.props['data-section-id']) {
        map[child.props['data-section-id']] = child;
      }
    });
    return map;
  }, [children]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
        <div className="space-y-6" data-testid="draggable-sections-container">
          {sectionOrder.map((sectionId) => {
            const child = childrenMap[sectionId];
            if (!child) return null;
            
            return (
              <SortableSection key={sectionId} id={sectionId}>
                {child}
              </SortableSection>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableSections;
